import { Stack, Typography } from '@mui/material'
import { com } from '@eidu/form'
import { useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import EntityTypeRepository from '../../io/EntityTypeRepository'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import getSemanticLinks from '../../api/analytics/getSemanticLinks'
import { requireNotUndefinedOrNull } from '../../util/require'
import AuthenticationContext, { createAuthContext } from '../../api/authorization/AuthenticationContext'
import { logMessage } from '../../util/Logging'
import EntityTypesStack from '../../components/layout/EntityTypesStack'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import SemanticLink = com.eidu.sharedlib.entity.analytics.SemanticLink

type SemanticsPageContentProps = {
  authContext: AuthenticationContext
  entityTypes: readonly EntityType[]
  semanticLinks: readonly SemanticLink[]
  onSemanticLinksChange: () => void
}

const SemanticsPageContent = ({
  authContext,
  entityTypes,
  semanticLinks,
  onSemanticLinksChange,
}: SemanticsPageContentProps) => (
  <Stack paddingX={3} paddingY={3} spacing={3}>
    <Typography variant="h4">Semantics</Typography>
    <EntityTypesStack
      authContext={authContext}
      kind={EntityTypeKind.School}
      entityTypes={entityTypes}
      semanticLinks={semanticLinks}
      onSemanticLinksChange={onSemanticLinksChange}
    />
    <EntityTypesStack
      authContext={authContext}
      kind={EntityTypeKind.User}
      entityTypes={entityTypes}
      semanticLinks={semanticLinks}
      onSemanticLinksChange={onSemanticLinksChange}
    />
  </Stack>
)

type SemanticsPageProps = {
  authContext: AuthenticationContext
  entityTypeRepository: EntityTypeRepository
}

const SemanticsPage = ({ authContext, entityTypeRepository }: SemanticsPageProps) => {
  const entityTypes = entityTypeRepository.useSortedEntityTypes() ?? []

  const [semanticLinks, setSemanticLinks] = useState<readonly SemanticLink[]>([])

  const [semanticLinksLoading, setSemanticLinksLoading] = useState(false)

  const handleSemanticLinksChange = useCallback(() => {
    setSemanticLinksLoading(true)
    getSemanticLinks({ authContext }).then(
      (links) => {
        setSemanticLinks(links)
        setSemanticLinksLoading(false)
      },
      (error) => {
        logMessage(`Error when getting semantic links: ${error}`, 'error', 'SemanticsPage')
        setSemanticLinksLoading(false)
      }
    )
  }, [authContext])

  useEffect(() => {
    handleSemanticLinksChange()
  }, [handleSemanticLinksChange])

  const isLoading = entityTypeRepository.useLoading() || semanticLinksLoading

  return (
    <>
      <SemanticsPageContent
        entityTypes={entityTypes}
        semanticLinks={semanticLinks}
        onSemanticLinksChange={handleSemanticLinksChange}
        authContext={authContext}
      />
      <LoadingOverlay isOpen={isLoading} />
    </>
  )
}

const SemanticsPageRoute = () => {
  const authContext = createAuthContext(useContext<IAuthContext>(AuthContext))
  const entityTypeRepository = useContext(EntityTypesContext)
  return (
    <SemanticsPage authContext={requireNotUndefinedOrNull(authContext)} entityTypeRepository={entityTypeRepository} />
  )
}

export default SemanticsPageRoute

export { SemanticsPageContent }
